import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  ChevronRight, ChevronDown, TrendingUp, TrendingDown, Minus,
  Sparkles, Search,
} from 'lucide-react'
import { api, formatMoney } from '@/lib/api'

type Lanc = {
  id: number
  data: string
  descricao: string
  valor: number
  tipo: string
  origem: string
  observacao: string
  conta_financeira_id: number | null
  marketplace_payment_id: string | null
  pedido_externo_numero: string | null
  numero_doc_externo: string | null
  valor_bruto_conta: number | null
  valor_liquido_conta: number | null
}

type Grupo = {
  codigo: string
  nome: string
  tipo: string
  total: number
  total_anterior: number
  variacao_pct: number | null
  qtd_lancamentos: number
  lancamentos: Lanc[]
  filhos: Grupo[]
}

type DREResp = {
  periodo: string
  anterior: string | null
  grupos: Grupo[]
  totalizadores: {
    receita: number
    deducao: number
    receita_liquida: number
    custo: number
    margem_bruta: number
    despesa_op: number
    margem_op: number
    despesa_fin: number
    lucro_liquido: number
    margem_pct: number
  }
}

const COR_TIPO: Record<string, string> = {
  receita: 'text-emerald-700',
  receita_fin: 'text-emerald-600',
  deducao: 'text-amber-700',
  custo: 'text-orange-700',
  despesa_op: 'text-red-700',
  despesa_fin: 'text-rose-700',
}

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

function VariacaoChip({ pct }: { pct: number | null }) {
  if (pct === null || pct === undefined) return <span className="text-ink-400 text-xs">—</span>
  const Icon = pct > 0 ? TrendingUp : pct < 0 ? TrendingDown : Minus
  const cor = pct > 0 ? 'text-emerald-600 bg-emerald-50' : pct < 0 ? 'text-red-600 bg-red-50' : 'text-ink-500 bg-ink-50'
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${cor}`}>
      <Icon size={10} />
      {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
    </span>
  )
}

function ChipOrigem({ origem }: { origem: string }) {
  const cores: Record<string, string> = {
    auto_mp: 'bg-sky-100 text-sky-800',
    baixa_marketplace: 'bg-amber-100 text-amber-800',
    baixa_cmv: 'bg-orange-100 text-orange-800',
    devolucao: 'bg-red-100 text-red-800',
    baixa: 'bg-ink-100 text-ink-700',
  }
  const labels: Record<string, string> = {
    auto_mp: 'Auto MP',
    baixa_marketplace: 'Marketplace',
    baixa_cmv: 'CMV',
    devolucao: 'Devolução',
    baixa: 'Baixa',
  }
  return <span className={`text-[9px] px-1.5 py-0.5 rounded font-semibold ${cores[origem] || 'bg-ink-100'}`}>{labels[origem] || origem}</span>
}

function LancamentoLinha({ l, busca }: { l: Lanc; busca: string }) {
  const [exp, setExp] = useState(false)
  const isReceita = l.tipo === 'receita'
  const matchBusca = !busca ||
    [l.descricao, l.observacao, l.numero_doc_externo, l.marketplace_payment_id, l.pedido_externo_numero]
      .filter(Boolean).join(' ').toLowerCase().includes(busca.toLowerCase())
  if (!matchBusca) return null
  const detalhe = l.observacao || ''
  const detalheCurto = detalhe.length > 80 ? detalhe.slice(0, 80) + '...' : detalhe

  return (
    <>
      <tr className="border-t border-ink-100 text-xs hover:bg-blue-50/40 cursor-pointer" onClick={() => setExp(!exp)}>
        <td className="py-1.5 pl-12 text-ink-500 font-mono whitespace-nowrap">{l.data.slice(8, 10)}/{l.data.slice(5, 7)}</td>
        <td className="py-1.5">
          <div className="flex items-center gap-2">
            {detalhe && (exp ? <ChevronDown size={11} className="text-ink-400" /> : <ChevronRight size={11} className="text-ink-400" />)}
            <span className="text-ink-700">{l.descricao}</span>
            <ChipOrigem origem={l.origem} />
          </div>
          {!exp && detalhe && (
            <div className="text-[10px] text-ink-500 italic mt-0.5 ml-3 truncate">{detalheCurto}</div>
          )}
        </td>
        <td className="py-1.5 font-mono text-[10px] whitespace-nowrap">
          {l.numero_doc_externo && <div className="text-ink-600">NF {l.numero_doc_externo}</div>}
          {l.pedido_externo_numero && (
            <a href={`https://www.mercadolivre.com.br/vendas/${l.pedido_externo_numero}/detalhe`} target="_blank" rel="noreferrer" className="text-amber-700 hover:underline" onClick={e => e.stopPropagation()}>
              ML {l.pedido_externo_numero}
            </a>
          )}
          {l.marketplace_payment_id && (
            <a href={`https://www.mercadopago.com.br/activities/${l.marketplace_payment_id}`} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline block" onClick={e => e.stopPropagation()}>
              MP {l.marketplace_payment_id}
            </a>
          )}
        </td>
        <td className={`py-1.5 text-right font-mono font-semibold whitespace-nowrap pr-3 ${isReceita ? 'text-emerald-700' : 'text-red-700'}`}>
          {isReceita ? '+' : '−'}{formatMoney(l.valor)}
        </td>
      </tr>
      {exp && detalhe && (
        <tr className="bg-ink-50/60 text-[11px]">
          <td colSpan={4} className="px-12 py-2 text-ink-700">
            <div className="font-semibold text-[10px] text-ink-500 uppercase mb-1">Composição / detalhes</div>
            <div className="whitespace-pre-wrap leading-relaxed">{detalhe}</div>
            {l.valor_bruto_conta && l.valor_liquido_conta && (
              <div className="mt-2 flex gap-4 text-[10px] text-ink-600">
                <span>Bruto conta: <strong className="font-mono">{formatMoney(l.valor_bruto_conta)}</strong></span>
                <span>Líquido conta: <strong className="font-mono text-emerald-700">{formatMoney(l.valor_liquido_conta)}</strong></span>
              </div>
            )}
          </td>
        </tr>
      )}
    </>
  )
}

function GrupoLinha({ g, nivel = 0, busca }: { g: Grupo; nivel?: number; busca: string }) {
  const [aberto, setAberto] = useState(nivel === 0 || (busca.length > 0))
  const temFilhos = g.filhos.length > 0 || g.lancamentos.length > 0
  const cor = COR_TIPO[g.tipo] || 'text-ink-700'
  const ind = nivel * 16

  return (
    <>
      <tr className={`border-t border-ink-200 ${nivel === 0 ? 'bg-ink-50 font-bold' : nivel === 1 ? 'bg-white' : ''}`}>
        <td className="py-2 pr-2" style={{ paddingLeft: 8 + ind }} colSpan={3}>
          <button
            onClick={() => temFilhos && setAberto(!aberto)}
            className={`flex items-center gap-1.5 text-left ${temFilhos ? 'cursor-pointer hover:underline' : 'cursor-default'}`}
          >
            {temFilhos
              ? aberto ? <ChevronDown size={14} className="text-ink-400" /> : <ChevronRight size={14} className="text-ink-400" />
              : <span className="w-3.5" />}
            <span className={`font-mono text-xs ${cor}`}>{g.codigo}</span>
            <span className={`${nivel === 0 ? 'text-sm uppercase tracking-wide' : 'text-sm'} ${cor}`}>{g.nome}</span>
            {g.qtd_lancamentos > 0 && (
              <span className="text-[10px] bg-ink-100 text-ink-600 px-1.5 py-0.5 rounded font-normal">
                {g.qtd_lancamentos} lanç
              </span>
            )}
          </button>
        </td>
        <td className="py-2 pr-3 text-right">
          <div className="flex items-center justify-end gap-2">
            <VariacaoChip pct={g.variacao_pct} />
            <span className={`font-mono ${nivel === 0 ? 'text-base font-bold' : 'text-sm font-semibold'} ${cor}`}>
              {formatMoney(g.total)}
            </span>
          </div>
        </td>
      </tr>
      {aberto && g.filhos.map(f => <GrupoLinha key={f.codigo} g={f} nivel={nivel + 1} busca={busca} />)}
      {aberto && g.lancamentos.length > 0 && g.lancamentos.map(l => <LancamentoLinha key={l.id} l={l} busca={busca} />)}
    </>
  )
}

function CardKpi({ label, valor, cor, tooltip }: any) {
  const cores: any = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    blue: 'border-blue-200 bg-blue-50 text-blue-900',
    violet: 'border-violet-200 bg-violet-50 text-violet-900',
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
  }
  const v = typeof valor === 'number' ? formatMoney(valor) : valor
  return (
    <div className={`relative group border rounded-lg p-2.5 ${cores[cor] || ''} ${tooltip ? 'cursor-help' : ''}`}>
      <div className="text-[10px] uppercase font-semibold opacity-70">{label}</div>
      <div className="text-lg font-bold mt-1 font-mono">{v}</div>
      {tooltip && (
        <div className="absolute z-50 left-0 top-full mt-1 hidden group-hover:block bg-ink-950 text-white text-[11px] rounded-md px-3 py-2 max-w-md whitespace-pre-wrap shadow-lg pointer-events-none">
          {tooltip}
        </div>
      )}
    </div>
  )
}

export default function DREPage() {
  const hoje = new Date()
  const [ano, setAno] = useState(hoje.getFullYear())
  const [mes, setMes] = useState(hoje.getMonth() + 1)
  const [busca, setBusca] = useState('')

  const { data, isLoading } = useQuery<DREResp>({
    queryKey: ['dre', ano, mes],
    queryFn: async () =>
      (await api.get('/financeiro/dre', { params: { ano, mes, comparar: true } })).data,
  })

  const insights = useMemo(() => {
    if (!data) return []
    const t = data.totalizadores
    const out: { titulo: string; valor: string; cor: 'green' | 'amber' | 'red'; obs: string }[] = []
    if (t.margem_pct >= 30) out.push({ titulo: 'Margem saudável', valor: `${t.margem_pct.toFixed(1)}%`, cor: 'green', obs: `Lucro líquido ${formatMoney(t.lucro_liquido)} sobre receita ${formatMoney(t.receita)}` })
    else if (t.margem_pct >= 15) out.push({ titulo: 'Margem moderada', valor: `${t.margem_pct.toFixed(1)}%`, cor: 'amber', obs: 'Atenção em despesas operacionais' })
    else out.push({ titulo: 'Margem baixa', valor: `${t.margem_pct.toFixed(1)}%`, cor: 'red', obs: 'Revisar custos e despesas' })

    const receitaML = data.grupos.find(g => g.codigo === '1')?.filhos.flatMap(f => f.filhos).find(s => s?.codigo === '1.1.1')?.total || 0
    const comissao = data.grupos.flatMap(g => g.filhos).find(f => f.codigo === '5.5')?.total || 0
    if (receitaML > 0) {
      const pct = (comissao / receitaML) * 100
      const cor = pct < 10 ? 'green' : pct < 14 ? 'amber' : 'red'
      out.push({ titulo: 'Taxa Marketplace', valor: `${pct.toFixed(1)}%`, cor, obs: `${formatMoney(comissao)} de comissão sobre ${formatMoney(receitaML)} de receita ML\nMédia mercado: ~13%` })
    }

    const dev = data.grupos.flatMap(g => g.filhos).find(f => f.codigo === '2.2')?.total || 0
    if (t.receita > 0) {
      const pct = (dev / t.receita) * 100
      const cor = pct < 2 ? 'green' : pct < 5 ? 'amber' : 'red'
      out.push({ titulo: 'Devoluções', valor: `${pct.toFixed(1)}%`, cor, obs: `${formatMoney(dev)} em devoluções (${pct.toFixed(2)}% da receita)` })
    }

    if (t.receita_liquida > 0 && t.custo > 0) {
      const pct = (t.custo / t.receita_liquida) * 100
      out.push({ titulo: 'CMV', valor: `${pct.toFixed(1)}%`, cor: pct < 50 ? 'green' : pct < 70 ? 'amber' : 'red', obs: `${formatMoney(t.custo)} em custos sobre ${formatMoney(t.receita_liquida)} de receita líquida` })
    }
    return out
  }, [data])

  const topLanc = useMemo(() => {
    if (!data) return [] as Lanc[]
    const todos: Lanc[] = []
    const walk = (g: Grupo) => {
      todos.push(...g.lancamentos)
      g.filhos.forEach(walk)
    }
    data.grupos.forEach(walk)
    return [...todos].sort((a, b) => b.valor - a.valor).slice(0, 10)
  }, [data])

  const t = data?.totalizadores

  return (
    <div className="p-4 lg:p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-ink-900 flex items-center gap-2">
            <Sparkles size={22} className="text-violet-600" /> DRE Inteligente
          </h1>
          <p className="text-xs text-ink-500">Hover nas KPIs mostra fórmula. Clique em qualquer linha pra expandir. Cada lançamento mostra origem, ML/MP, NF e composição.</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-ink-400" />
            <input
              className="input pl-7 w-[260px]"
              placeholder="Buscar lançamento, NF, MP id..."
              value={busca}
              onChange={e => setBusca(e.target.value)}
            />
          </div>
          <select className="input w-[100px]" value={mes} onChange={e => setMes(Number(e.target.value))}>
            {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select className="input w-[100px]" value={ano} onChange={e => setAno(Number(e.target.value))}>
            {[ano - 2, ano - 1, ano, ano + 1].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </div>

      {isLoading || !data ? (
        <div className="p-12 text-center text-ink-400">Carregando DRE...</div>
      ) : (
        <>
          {insights.length > 0 && (
            <div className="bg-gradient-to-r from-violet-50 to-blue-50 border border-violet-200 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={14} className="text-violet-600" />
                <span className="text-xs font-semibold uppercase text-violet-900">Insights automáticos</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {insights.map((i, idx) => (
                  <div key={idx} className={`relative group bg-white border rounded p-2 cursor-help ${
                    i.cor === 'green' ? 'border-emerald-200' : i.cor === 'amber' ? 'border-amber-200' : 'border-red-200'
                  }`}>
                    <div className="text-[10px] text-ink-500 uppercase">{i.titulo}</div>
                    <div className={`text-base font-bold font-mono ${
                      i.cor === 'green' ? 'text-emerald-700' : i.cor === 'amber' ? 'text-amber-700' : 'text-red-700'
                    }`}>{i.valor}</div>
                    <div className="absolute z-50 left-0 top-full mt-1 hidden group-hover:block bg-ink-950 text-white text-[11px] rounded px-3 py-2 max-w-xs whitespace-pre-wrap shadow-lg pointer-events-none">
                      {i.obs}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
            <CardKpi label="Receita Bruta" valor={t!.receita} cor="emerald" />
            <CardKpi label="Receita Líquida" valor={t!.receita_liquida} cor="emerald" tooltip={`Receita − Deduções\n${formatMoney(t!.receita)} − ${formatMoney(t!.deducao)} = ${formatMoney(t!.receita_liquida)}`} />
            <CardKpi label="Margem Bruta" valor={t!.margem_bruta} cor="blue" tooltip={`Receita Líquida − CMV\n${formatMoney(t!.receita_liquida)} − ${formatMoney(t!.custo)} = ${formatMoney(t!.margem_bruta)}`} />
            <CardKpi label="Margem Operacional" valor={t!.margem_op} cor="blue" tooltip={`Margem Bruta − Despesas Op\n${formatMoney(t!.margem_bruta)} − ${formatMoney(t!.despesa_op)} = ${formatMoney(t!.margem_op)}`} />
            <CardKpi label="Lucro Líquido" valor={t!.lucro_liquido} cor="violet" tooltip={`Margem Op − Despesas Financeiras\n${formatMoney(t!.margem_op)} − ${formatMoney(t!.despesa_fin)} = ${formatMoney(t!.lucro_liquido)}`} />
            <CardKpi label="Margem %" valor={`${t!.margem_pct.toFixed(1)}%`} cor="amber" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 bg-white border border-ink-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-ink-100 border-b-2 border-ink-200">
                  <tr className="text-xs font-semibold text-ink-600 uppercase">
                    <th className="text-left py-2 pl-3" colSpan={3}>Conta / Lançamento</th>
                    <th className="text-right py-2 pr-3">Total {data.periodo}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.grupos.map(g => <GrupoLinha key={g.codigo} g={g} busca={busca} />)}
                  <tr className="border-t-2 border-ink-300 bg-violet-50 font-bold">
                    <td className="py-3 pl-3 text-violet-900 uppercase tracking-wide" colSpan={3}>= Lucro Líquido</td>
                    <td className="py-3 pr-3 text-right font-mono text-lg text-violet-900">{formatMoney(t!.lucro_liquido)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="bg-white border border-ink-200 rounded-lg p-3">
              <div className="text-xs font-semibold text-ink-600 uppercase mb-2 flex items-center gap-1.5">
                <TrendingUp size={12} /> Top 10 lançamentos
              </div>
              <div className="space-y-1.5 max-h-[600px] overflow-y-auto">
                {topLanc.map(l => (
                  <div key={l.id} className="flex items-start justify-between gap-2 p-2 rounded hover:bg-blue-50 text-xs">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <ChipOrigem origem={l.origem} />
                        <span className="text-[9px] text-ink-500 font-mono">{l.data.slice(8, 10)}/{l.data.slice(5, 7)}</span>
                      </div>
                      <div className="text-ink-700 truncate">{l.descricao}</div>
                      {l.observacao && (
                        <div className="text-[10px] text-ink-500 truncate italic">{l.observacao.slice(0, 60)}{l.observacao.length > 60 ? '...' : ''}</div>
                      )}
                    </div>
                    <div className={`font-mono font-semibold whitespace-nowrap ${l.tipo === 'receita' ? 'text-emerald-700' : 'text-red-700'}`}>
                      {l.tipo === 'receita' ? '+' : '−'}{formatMoney(l.valor)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
